import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getFormattedDateTime } from 'app/core/shared/helper/date.helper';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import {
    FieldHealthinessListResponse,
    HealthinessSummaryResponse,
    PaginateResponse,
    ProductionStatusCategoryResponse,
    Response,
    StringHealthinessListResponse,
    StringsHealthinessSummaryResponse,
    WellHealthinessCategoryResponse,
    WellHealthinessStatus,
} from 'app/modules/well-healthiness/model';
import { Observable, take, tap } from 'rxjs';
import { environment } from 'environments/environment';


@Injectable({ providedIn: 'root' })
export class WellHealthinessService {
    constructor(private readonly _httpClient: HttpClient) { }

    getHealthinessTable(
        params: any
    ): Observable<Response<PaginateResponse<WellHealthinessStatus>>> {
        const url = composeUrl(
            `/v1/WellHealthiness/GetWellHealthinessList`,
            params
        );
        return this._httpClient
            .get<Response<PaginateResponse<WellHealthinessStatus>>>(url)
            .pipe(take(1));
    }

    downloadWellHealthiness() {
        const { date, time } = getFormattedDateTime();
        const url = composeUrl(
            `/v1/WellHealthiness/DownloadOverallWellHealthinessExcel`
        );
        this._httpClient
            .get(url, { responseType: 'blob' })
            .subscribe((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Well_Healthiness_Records_${date}_${time}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            });
    }

    getOpenCMRecords(params: any): Observable<Response<any>> {
        const url = composeUrl(`/v1/WellHealthiness/GetOpenCMRecords`, params);
        return this._httpClient.get<Response<any>>(url).pipe(take(1));
    }

    getWellsHealthinessSummary(): Observable<HealthinessSummaryResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/wells/summary`
        );
        return this._httpClient
            .get<HealthinessSummaryResponse>(url)
            .pipe(take(1));
    }

    getStringsHealthinessSummary(): Observable<StringsHealthinessSummaryResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/strings/summary`
        );
        return this._httpClient
            .get<StringsHealthinessSummaryResponse>(url)
            .pipe(take(1));
    }

    getWellHealthinessFieldsList(
        params: any
    ): Observable<FieldHealthinessListResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/fields/list`,
            params
        );
        return this._httpClient
            .get<FieldHealthinessListResponse>(url)
            .pipe(take(1));
    }

    getWellHealthinessStringsList(
        params: any
    ): Observable<StringHealthinessListResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/strings/list`,
            params
        );
        return this._httpClient
            .get<StringHealthinessListResponse>(url)
            .pipe(take(1));
    }

    getWellHealthinessCategoryByWellHealthiness(): Observable<WellHealthinessCategoryResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/category/by-well-healthiness`
        );
        return this._httpClient
            .get<WellHealthinessCategoryResponse>(url)
            .pipe(take(1));
    }

    getWellHealthinessCategoryByProductionStatus(): Observable<ProductionStatusCategoryResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/category/by-production-status`
        );
        return this._httpClient
            .get<ProductionStatusCategoryResponse>(url)
            .pipe(take(1));
    }

    getWellHealthinessFieldsSummaryExport(): Observable<Blob> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/fields/summary/export`
        );
        return this._httpClient
            .get<Blob>(url, { responseType: 'blob' as 'json' })
            .pipe(take(1));
    }

    getWellHealthinessStringsListExport(params?: any): Observable<Blob> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/well-healthiness/strings/list/export`,
            params
        );
        return this._httpClient
            .get<Blob>(url, { responseType: 'blob' as 'json' })
            .pipe(take(1));
    }

    getOnHoldWellHealthinessRequest(
        params: any
    ): Observable<any> {
        const url = composeUrl(
            `/v1/WellHealthiness/GetOnHoldWellHealtiness`,
            params
        );
        return this._httpClient
            .get<any>(url)
            .pipe(take(1));
    }

    manualOverrideWellHealthiness(payload) {
        return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/WellHealthiness/ManualOverrideWellHealtiness`, payload).pipe(
            tap((response: any) => {
                return response
            }),
        );
    }

    getWellHealthinessHistorical(params: any): Observable<any> {
      const url = composeUrl(
            `/v1/WellHealthiness/GetWellHealthinessHistorical`,
            params
        );
        return this._httpClient
            .get<any>(url)
            .pipe(take(1));
    }
}
